import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import './App.css';

function App() {
  const [inputText, setInputText] = useState('');
  const [displayQrCodeDataUrl, setDisplayQrCodeDataUrl] = useState('');
  const [qrCodeWidth, setQrCodeWidth] = useState(300);
  const [qrCodeColor, setQrCodeColor] = useState('#000000');
  const [error, setError] = useState('');

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  const generateDisplayQRCode = async (text, color) => {
    if (!text.trim()) {
      setDisplayQrCodeDataUrl('');
      setError('');
      return;
    }

    try {
      setError('');
      const dataUrl = await QRCode.toDataURL(text, {
        width: 200,
        margin: 1,
        color: {
          dark: color,
          light: '#FFFFFF'
        }
      });
      setDisplayQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('QRコードの生成に失敗しました:', error);
      setError('QRコードの生成に失敗しました。テキストを確認してください。');
      setDisplayQrCodeDataUrl('');
    }
  };

  const generateDownloadQRCode = async (text, width, color) => {
    if (!text.trim()) {
      return null;
    }

    try {
      const margin = 2;
      const modulePixelSize = 10;
      const qrCodeSize = width - (margin * 2 * modulePixelSize);
      
      const dataUrl = await QRCode.toDataURL(text, {
        width: Math.max(qrCodeSize, 50),
        margin: margin,
        color: {
          dark: color,
          light: '#FFFFFF'
        }
      });
      return dataUrl;
    } catch (error) {
      console.error('QRコードの生成に失敗しました:', error);
      setError('ダウンロード用QRコードの生成に失敗しました。');
      return null;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGenerateQR = useCallback(
    debounce((text, color) => generateDisplayQRCode(text, color), 300),
    []
  );

  useEffect(() => {
    debouncedGenerateQR(inputText, qrCodeColor);
  }, [inputText, qrCodeColor, debouncedGenerateQR]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    const MAX_TEXT_LENGTH = 2000;
    
    if (value.length > MAX_TEXT_LENGTH) {
      setError(`テキストが長すぎます。${MAX_TEXT_LENGTH}文字以内で入力してください。`);
      return;
    }
    
    setError('');
    setInputText(value);
  };

  const handleWidthChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0 && value <= 1000) {
      setQrCodeWidth(value);
    }
  };

  const handleColorChange = (color) => {
    setQrCodeColor(color);
  };

  const handleDownload = async () => {
    if (inputText.trim()) {
      const downloadDataUrl = await generateDownloadQRCode(inputText, qrCodeWidth, qrCodeColor);
      if (downloadDataUrl) {
        const link = document.createElement('a');
        link.download = 'qr.png';
        link.href = downloadDataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const colorPalette = [
    { name: '黒', color: '#000000' },
    { name: '赤', color: '#FF0000' },
    { name: '青', color: '#0000FF' },
    { name: '緑', color: '#00FF00' },
    { name: '紫', color: '#800080' },
    { name: 'オレンジ', color: '#FFA500' },
    { name: 'ピンク', color: '#FF69B4' },
    { name: '茶', color: '#8B4513' },
    { name: 'グレー', color: '#808080' },
    { name: 'ネイビー', color: '#000080' }
  ];

  return (
    <div className="App">
      <header className="App-header">
        <h1>QRコード生成ツール</h1>
        <div className="qr-container">
          <div className="input-section">
            <label htmlFor="text-input">QRコード化したい文字を入力してください</label>
            <textarea
              id="text-input"
              value={inputText}
              onChange={handleInputChange}
              rows={4}
              cols={50}
              maxLength={2000}
            />
            <div className="character-count">
              {inputText.length}/2000文字
            </div>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <div className="width-setting">
              <label htmlFor="width-input">QRコードの幅 (px):</label>
              <input
                id="width-input"
                type="number"
                value={qrCodeWidth}
                onChange={handleWidthChange}
                min="50"
                max="1000"
                step="10"
                placeholder="300"
              />
              <span className="width-range">50px - 1000px</span>
            </div>

            <div className="color-setting">
              <label>QRコードの色:</label>
              <div className="color-palette">
                {colorPalette.map((colorItem) => (
                  <button
                    key={colorItem.color}
                    className={`color-button ${qrCodeColor === colorItem.color ? 'selected' : ''}`}
                    style={{ backgroundColor: colorItem.color }}
                    onClick={() => handleColorChange(colorItem.color)}
                    title={colorItem.name}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <div className="qr-display">
            {displayQrCodeDataUrl ? (
              <div className="qr-result">
                <img src={displayQrCodeDataUrl} alt={`QRコード: ${inputText}`} />
                <button 
                  className="download-button"
                  onClick={handleDownload}
                >
                  ダウンロード
                </button>
              </div>
            ) : (
              <div className="qr-placeholder">
                <p>文字列を入力するとQRコードが表示されます！</p>
              </div>
            )}
          </div>
        </div>
      </header>
      <footer className="app-footer">
        <div className="footer-inner">
          <p>
            <a 
              href="https://comodo-apps.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="footer-link"
            >
              Comodo Apps
            </a>
          </p>
          <p>
            <span className="copyright">© 2025 Comodo Apps</span>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
